import React from "react";
import { Flame, Target, Eye, Gem, BookOpen } from "lucide-react";
import { useFetch } from "@/lib/api";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { Reveal } from "@/components/blocks/Reveal";
import { RichTextContent } from "@/components/blocks/RichTextContent";

const Block = ({ icon: Icon, title, children, placeholder, richText = false }) => (
  <Reveal className="border border-border bg-card p-8">
    <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-navy text-brand-orange">
      <Icon className="h-5 w-5" />
    </span>
    <h3 className="mt-5 font-display text-xl font-semibold text-navy">{title}</h3>
    {children ? (
      richText ? (
        <RichTextContent
          className="mt-3 text-sm leading-relaxed text-muted-foreground"
          html={children}
          testId={`${title.toLowerCase().replaceAll(" ", "-")}-rich-text`}
        />
      ) : (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{children}</p>
      )
    ) : (
      <p className="mt-3 text-sm italic leading-relaxed text-muted-foreground/70">{placeholder}</p>
    )}
  </Reveal>
);

const About = () => {
  const { data: m } = useFetch("/ministry");
  if (!m) return <div className="min-h-screen" />;

  return (
    <div>
      <PageHero overline="About the Ministry" title="Who We Are" subtitle={m.type} image={m.images?.bible} />

      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
            <div>
              <SectionHeading overline="Who We Are" title="An international, interdenominational evangelism ministry" />
              <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">{m.who_we_are}</p>
            </div>
            <div>
              <SectionHeading overline="Our History" title="Est. 2005" />
              <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">{m.history}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mandate + Scripture */}
      <section className="bg-navy py-24 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="overline text-brand-orange">Our Mandate</span>
            <p className="mt-6 font-display text-3xl font-light leading-snug text-white md:text-4xl">“{m.mandate}”</p>
            <div className="mt-6 flex items-center gap-3 text-white/60">
              <Flame className="h-5 w-5 text-brand-orange" />
              <span className="font-display italic">“{m.mandate_imagery}”</span>
            </div>
          </div>
          <div className="border-l border-white/10 pl-8 lg:pl-16">
            <span className="overline text-brand-orange">Our Key Scripture</span>
            <p className="mt-6 font-display text-2xl font-light leading-relaxed text-white md:text-3xl">{m.scripture_text}</p>
            <p className="mt-6 font-display text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">{m.scripture_reference}</p>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="bg-secondary/40 py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline="Our Foundation" title="Mission, Vision & Values" description="These statements will be published as officially provided by the ministry." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <Block icon={Target} title="Our Mission" placeholder={m.mission_placeholder} richText>
              {m.mission || null}
            </Block>
            <Block icon={Eye} title="Our Vision" placeholder={m.vision_placeholder} richText>
              {m.vision || null}
            </Block>
            <Block icon={Gem} title="Our Values" placeholder={m.values_placeholder}>
              {m.values && m.values.length ? m.values.join(", ") : null}
            </Block>
          </div>
        </div>
      </section>

      {/* Follow-up summary */}
      {m.followup && (
        <section className="bg-white py-24 md:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHeading overline="Follow-Up & Discipleship" title={m.followup.title} description={m.followup.intro} />
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {m.followup.principles.map((p) => (
                <div key={p.title} className="border border-border bg-card p-8">
                  <BookOpen className="h-8 w-8 text-brand-orange" />
                  <h3 className="mt-5 font-display text-lg font-semibold text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
